import type { ErrorEvent } from '@sentry/browser';

import {
	extractChunkUrlFromMessage,
	getStaticAssetUrl,
} from 'src/modules/chunk-url';

type ChunkEvent = Pick<ErrorEvent, 'exception' | 'tags' | 'extra' | 'fingerprint'>;

const CHUNK_SAMPLE_RATE = 0.01;
const PROBE_TIMEOUT_MS = 1_000;

export const isChunkErrorEvent = (event: ChunkEvent): boolean =>
	(event.exception?.values ?? []).some(
		exception =>
			exception.type === 'ChunkLoadError' ||
			(exception.value?.includes('Loading chunk') ?? false) ||
			(exception.value?.includes('Loading CSS chunk') ?? false)
	);

export const shouldDropChunkError = (event: ChunkEvent): boolean =>
	isChunkErrorEvent(event) && Math.random() >= CHUNK_SAMPLE_RATE;

const extractChunkUrl = (event: ChunkEvent): string | undefined => {
	for (const exception of event.exception?.values ?? []) {
		const chunkUrl = extractChunkUrlFromMessage(exception.value);
		if (chunkUrl) return chunkUrl;
	}
	return undefined;
};

const getResponseStatus = (response: Response, assetType: string): string => {
	if (response.status === 404 || response.status === 410) return 'not_found';
	if (!response.ok) return 'http_error';
	const mime = response.headers.get('content-type')?.split(';')[0].trim().toLowerCase();
	if (!mime) return 'missing_content_type';
	if (mime === 'text/html' || mime === 'application/xhtml+xml') return 'html_response';
	const matchesAsset = assetType === 'css'
		? mime === 'text/css'
		: /^(text|application)\/(javascript|ecmascript|x-javascript)$/.test(mime);
	return matchesAsset ? 'accessible' : 'unexpected_content_type';
};

const probeChunk = async (url: URL, assetType: string): Promise<Record<string, string>> => {
	const controller = new AbortController();
	let timeout: ReturnType<typeof setTimeout> | undefined;
	try {
		// A consulta é posterior à falha: acessibilidade agora não prova sua causa.
		return await Promise.race([
			fetch(url.href, {
				method: 'HEAD',
				cache: 'no-store',
				redirect: 'error',
				signal: controller.signal,
			}).then(response => ({
				'chunk_error.probe_status': getResponseStatus(response, assetType),
				'chunk_error.http_status': String(response.status),
				'chunk_error.content_type': response.headers.get('content-type') ?? 'missing',
			})),
			new Promise<Record<string, string>>(resolve => {
				timeout = setTimeout(() => {
					resolve({ 'chunk_error.probe_status': 'timeout' });
					controller.abort();
				}, PROBE_TIMEOUT_MS);
			}),
		]);
	} catch {
		return { 'chunk_error.probe_status': 'fetch_failed' };
	} finally {
		if (timeout !== undefined) clearTimeout(timeout);
	}
};

/** Enriquece somente a amostra aceita; não captura outro evento nem recarrega a página. */
export const applyChunkErrorContext = async <T extends ChunkEvent>(event: T): Promise<T> => {
	const chunkUrl = extractChunkUrl(event);
	const probeUrl = chunkUrl ? getStaticAssetUrl(chunkUrl) : null;
	const isCss = (event.exception?.values ?? []).some(
		exception => exception.value?.includes('Loading CSS chunk')
	) || probeUrl?.pathname.startsWith('/static/css/');
	const assetType = isCss ? 'css' : 'js';
	const chunkName = probeUrl?.pathname.split('/').pop()
		?.replace(/(?:\.[a-f0-9]{8,})?\.chunk\.(css|js)$/i, '') ?? 'unknown';

	event.tags = {
		...event.tags,
		'chunk_error.asset_type': assetType,
		'chunk_error.chunk_name': chunkName,
		'chunk_error.sample_rate': String(CHUNK_SAMPLE_RATE),
		'chunk_error.likely_cause': 'unknown',
	};
	event.extra = {
		...event.extra,
		chunk_url: chunkUrl ?? 'not_identified',
		page_open_seconds: Math.round(performance.now() / 1000),
	};
	event.fingerprint = ['chunk-load', assetType, chunkName];

	const result = probeUrl
		? await probeChunk(probeUrl, assetType)
		: { 'chunk_error.probe_status': chunkUrl ? 'skipped_untrusted_url' : 'missing_url' };
	event.tags = {
		...event.tags,
		...result,
		'chunk_error.chunk_url_status': result['chunk_error.probe_status'],
	};
	return event;
};
